'use client'

import * as React from 'react'
import { Suspense } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { Bookmark, AlertCircle, X, ArrowUp, Search, BedDouble, Banknote, Maximize2, MapPin, Home, Tag, Clock, Layers, ShieldCheck, Pin, CalendarClock, Settings, EyeOff, Eye, RotateCcw, LayoutGrid, Map as MapIcon, Grip, type LucideIcon } from 'lucide-react'
import { getIconComponent } from '@/lib/feature-icons'
import { SortSelect, SaveSearchDialog, MobileViewToggle, MobileQuickFilters, JumpWaypoints } from '@/components/search'
import { Seg, RMenu, PageNav, fmtCZK } from '@/components/roostr'
import { ListingsGrid } from '@/components/listings'
import { ListingMapGoogle, MapDistrictList, MapSidePanel, MapListHandle } from '@/components/map'
import { RateLimitBanner, Badge } from '@/components/ui'
import { useAuth } from '@/components/auth'
import { useSearchStore, useUIStore, useKeywordsStore, useUserStore, useSettingsStore } from '@/lib/store'
import { api, ApiError } from '@/lib/api'
import { RERUN_SEARCH_EVENT } from '@/lib/apply-search'
import { REQUIRE_AUTH_FOR_SEARCH } from '@/lib/feature-flags'
import { FILTER_TAGS, STATIC_FILTER_TAGS, SORT_OPTIONS, GRID_COLUMN_STOPS, gridStopIndexForCardSize, gridColumnsForCardSize } from '@/lib/constants'
import type { Listing, MapPoint, MapPointsRequest, RentalFilters, SearchRequest, SortOption } from '@/lib/types'
import { cn } from '@/lib/utils'
import { createRecentSearch } from '@/lib/search-history'
import { addRecentSearch as addRecentSearchFirestore } from '@/lib/firebase'
import { useI18n } from '@/lib/i18n'

// Sorts whose key has a user-meaningful value at any position — for these,
// deep pagination can be anchored to values ("~27 000 Kč starts at p. 155")
// instead of bare page numbers.
const SORT_JUMP_KIND: Partial<Record<SortOption, 'price' | 'date' | 'area' | 'rooms'>> = {
  price_asc: 'price',
  price_desc: 'price',
  newest: 'date',
  oldest: 'date',
  area_asc: 'area',
  area_desc: 'area',
  rooms_asc: 'rooms',
  rooms_desc: 'rooms',
}

function SearchPageContent() {
  const { t, uiLanguage } = useI18n()
  const searchParams = useSearchParams()

  const {
    query,
    setQuery,
    selectedCity,
    setSelectedCity,
    selectedDistricts,
    setSelectedDistricts,
    filters,
    activeTags,
    addTag,
    removeTag,
    clearTags,
    sort,
    limit,
    travelTime,
    setTravelTime,
    viewMode,
    setViewMode,
    hiddenListings,
    showListing,
    showAllHidden,
    results,
    setResults,
    totalCount,
    setTotalCount,
    isLoading,
    setIsLoading,
    isLoadingMore,
    setIsLoadingMore,
    hasSearched,
    setHasSearched,
    updateFilter,
    clearFilters,
    setOffset,
    setHighlightedKeywords,
    setReferenceLocation,
  } = useSearchStore()
  const { country } = useUIStore()
  const { user, signIn } = useAuth()
  const { addRecentSearch } = useUserStore()
  const { dealbreakers, listingFreshness, defaultSort, defaultViewMode, defaultPriceMode, defaultCity, searchHistoryEnabled, cardSize, setCardSize } = useSettingsStore()

  // Apply settings defaults on first load
  const settingsApplied = React.useRef(false)
  React.useEffect(() => {
    if (settingsApplied.current) return
    settingsApplied.current = true
    
    // Apply default sort if user hasn't changed it from the store's own default
    const store = useSearchStore.getState()
    if (store.sort === 'newest' && defaultSort !== 'newest') {
      store.setSort(defaultSort)
    }
    // Apply default view mode
    if (store.viewMode === 'list' && defaultViewMode !== 'list') {
      store.setViewMode(defaultViewMode)
    }
    // Apply default price mode
    if (defaultPriceMode !== store.filters.use_total_price) {
      store.updateFilter('use_total_price', defaultPriceMode)
    }
    // Apply default city
    if (store.selectedCity === 'Praha' && defaultCity !== 'Praha') {
      store.setSelectedCity(defaultCity)
    }
  }, [defaultSort, defaultViewMode, defaultPriceMode, defaultCity])

  // Restore view + page state from the URL (written by the sync effect below),
  // so back-navigation and re-opened tabs land where the user left off instead
  // of grid page 1. The page refs are consumed once — by the init search (list
  // page) and the map auto-load (map page). Declared after the settings
  // defaults so an explicit URL state wins over defaultViewMode.
  const pendingListPageRef = React.useRef<number | null>(null)
  const restoredFromUrlRef = React.useRef(false)
  React.useEffect(() => {
    if (restoredFromUrlRef.current) return
    restoredFromUrlRef.current = true
    const page = parseInt(searchParams.get('page') || '', 10)
    if (Number.isFinite(page) && page > 1) pendingListPageRef.current = page
    const view = searchParams.get('view')
    const store = useSearchStore.getState()
    if (view === 'map' && store.viewMode !== 'map') store.setViewMode('map')
    if (view === 'list' && store.viewMode !== 'list') store.setViewMode('list')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Migrate persisted cardSize values from older builds whose slider used a
  // different range: clamp AND write back once so the toolbar thumb and the
  // grid (which reads the raw store value) agree.
  React.useEffect(() => {
    const snapped = GRID_COLUMN_STOPS[gridStopIndexForCardSize(cardSize)].size
    if (snapped !== cardSize) setCardSize(snapped)
  }, [cardSize, setCardSize])

  // Save search dialog state
  const [saveDialogOpen, setSaveDialogOpen] = React.useState(false)

  // Split map view: selected listing + card refs for scroll-to
  const [selectedMapListingId, setSelectedMapListingId] = React.useState<string | null>(null)
  // Re-clicking the same card must re-centre it, so carry a nonce alongside.
  const [mapFocusNonce, setMapFocusNonce] = React.useState(0)
  const focusMapListing = React.useCallback((id: string) => {
    setSelectedMapListingId(id)
    setMapFocusNonce((n) => n + 1)
  }, [])
  const cardRefsMap = React.useRef<Map<string, HTMLDivElement>>(new Map())
  const [hoveredMapListingId, setHoveredMapListingId] = React.useState<string | null>(null)
  // Pin hover → scroll the side panel to that card. Only for hovers that
  // ORIGINATE on the map: hovering a card also sets hoveredMapListingId, and
  // scrolling the panel under the user's own cursor would fight them.
  // Debounced so sweeping the pointer across a dense pin field doesn't thrash
  // the list; cancelled on leave.
  const pinHoverScrollRef = React.useRef<number | null>(null)
  const handlePinHover = React.useCallback((id: string | null) => {
    setHoveredMapListingId(id)
    if (pinHoverScrollRef.current) {
      window.clearTimeout(pinHoverScrollRef.current)
      pinHoverScrollRef.current = null
    }
    if (!id) return
    pinHoverScrollRef.current = window.setTimeout(() => {
      const el = cardRefsMap.current.get(id)
      // `nearest` (not `center`): if the card is already visible nothing
      // moves; if it's off the panel it slides just far enough to show it.
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 120)
  }, [])
  // District-header hover → matching pins glow (design search.jsx:330, 626-635)
  const [hoveredMapDistrict, setHoveredMapDistrict] = React.useState<string | null>(null)

  // Error state for user-friendly error messages
  const [searchError, setSearchError] = React.useState<string | null>(null)

  // The query that was last submitted (not the live input value)
  const [searchedQuery, setSearchedQuery] = React.useState<string>('')
  const [currentPage, setCurrentPage] = React.useState(1)

  // Rate limit state
  const [rateLimitInfo, setRateLimitInfo] = React.useState<{
    retryAfter: number
    dayRemaining: number
    dayLimit: number
  } | null>(null)

  // Initial search flag
  const hasInitialSearched = React.useRef(false)

  // Flag to trigger re-search after a chip is removed (set before state update, consumed in effect)
  const chipRemovedRef = React.useRef(false)

  // Flag to trigger re-search after a tag is added from listing card
  const tagAddedRef = React.useRef(false)

  // Track what initiated the current/last search
  const searchSourceRef = React.useRef<'text' | 'filters' | 'chip' | 'sort' | 'init'>('init')

  // When REQUIRE_AUTH_FOR_SEARCH blocks a search attempt (anonymous user), remember
  // which source it was so we can retry automatically once they sign in via the modal
  // handleSearch itself opened — otherwise a successful sign-in leaves the page blank
  // since hasInitialSearched is already marked true and nothing else re-triggers it.
  const pendingAuthRetrySourceRef = React.useRef<'text' | 'filters' | 'chip' | 'sort' | 'init' | null>(null)

  // One-shot filter: when loading a saved search with "Show Only New", only show listings newer than this date
  const newSinceRef = React.useRef<string | null>(null)
  // One-shot filter: when opening from notifications, only show notification listing IDs
  const notificationListingIdsRef = React.useRef<string[] | null>(null)

  // Store LLM-parsed criteria from text search (independent of filter UI)
  // When set, chips are derived from this; when null, chips come from filter state.
  const [parsedCriteria, setParsedCriteria] = React.useState<{
    rooms_min?: number
    rooms_max?: number
    price_min?: number
    price_max?: number
    area_min?: number
    area_max?: number
    floor_min?: number
    floor_max?: number
    city?: string
    districts?: string[]
    type?: string
    transaction_type?: string
    keywords: string[]
    semanticQuery: string | null
  } | null>(null)

  // Clear rate limit banner when user logs in
  React.useEffect(() => {
    if (user && rateLimitInfo) {
      setRateLimitInfo(null)
    }
  }, [user, rateLimitInfo])

  // Back to top button visibility
  const [showBackToTop, setShowBackToTop] = React.useState(false)

  // Track scroll position for back-to-top button
  React.useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Build filters from active tags
  const getFiltersFromTags = React.useCallback((): Partial<RentalFilters> => {
    const tagFilters: Partial<RentalFilters> = {}

    activeTags.forEach((tagId) => {
      const tag = FILTER_TAGS.find((t) => t.id === tagId)
      if (tag) {
        (tagFilters as Record<string, unknown>)[tag.value.field] = tag.value.value
      }
    })

    return tagFilters
  }, [activeTags])

  // Build human-readable filter chips for the results header.
  // Chips are derived from parsedCriteria (text mode) or filter state (filter mode).
  type ChipCategory = 'rooms' | 'price' | 'area' | 'floor' | 'location' | 'type' | 'feature' | 'travel'
  const filterChips = React.useMemo(() => {
    const chips: { label: string; category: ChipCategory; icon?: LucideIcon; onRemove: () => void }[] = []
    const fmt = (n: number) => n.toLocaleString('cs-CZ')
    const { filterTags, keywords: keywordsData } = useKeywordsStore.getState()
    const allTags = filterTags.length > 0 ? filterTags : FILTER_TAGS

    // Build keyword definition map for icon resolution (same pattern as use-listing-amenities)
    const allKeywordDefs = keywordsData?.categories
      ? Object.values(keywordsData.categories).flatMap((cat) => cat.keywords)
      : []
    const keywordDefMap = new Map(allKeywordDefs.map((kw) => [kw.feature_key, kw]))

    // Helper: when removing a chip from text mode, convert remaining parsedCriteria
    // to filter state, clear parsedCriteria + query, and trigger chip-removal re-search.
    const commitParsedWithout = (
      remover: (pc: NonNullable<typeof parsedCriteria>) => Partial<NonNullable<typeof parsedCriteria>>
    ) => {
      chipRemovedRef.current = true
      if (!parsedCriteria) return
      const remaining = { ...parsedCriteria, ...remover(parsedCriteria) }

      // Apply remaining criteria to filter state
      updateFilter('rooms_min', remaining.rooms_min)
      updateFilter('rooms_max', remaining.rooms_max)
      updateFilter('price_min', remaining.price_min)
      updateFilter('price_max', remaining.price_max)
      updateFilter('area_min', remaining.area_min)
      updateFilter('area_max', remaining.area_max)
      updateFilter('floor_min', remaining.floor_min)
      updateFilter('floor_max', remaining.floor_max)
      if (remaining.city) setSelectedCity(remaining.city)
      else setSelectedCity('Praha')
      if (remaining.districts?.length) setSelectedDistricts(remaining.districts)
      else setSelectedDistricts([])
      if (remaining.type) updateFilter('type', remaining.type as 'apartment' | 'room' | 'house')
      if (remaining.transaction_type === 'rent' || remaining.transaction_type === 'sale') {
        updateFilter('transaction_type', remaining.transaction_type)
      } else {
        updateFilter('transaction_type', 'rent')
      }

      // Add remaining keywords as active tags
      clearTags()
      remaining.keywords?.forEach((kw) => addTag(kw))

      // Clear text mode state
      setParsedCriteria(null)
      setQuery('')
    }

    if (parsedCriteria) {
      // TEXT MODE: build chips from parsedCriteria
      const pc = parsedCriteria

      // Rooms
      const rMin = pc.rooms_min
      const rMax = pc.rooms_max
      const removeRooms = () => commitParsedWithout(() => ({ rooms_min: undefined, rooms_max: undefined }))
      if (rMin != null && rMax != null) {
        const label = rMin === rMax ? `${rMin} ${rMin !== 1 ? t('listingCard.roomMany', 'rooms') : t('listingCard.roomOne', 'room')}` : `${rMin}\u2013${rMax} ${t('listingCard.roomMany', 'rooms')}`
        chips.push({ label, category: 'rooms', onRemove: removeRooms })
      } else if (rMin != null) {
        chips.push({ label: `${rMin}+ ${t('listingCard.roomMany', 'rooms')}`, category: 'rooms', onRemove: removeRooms })
      } else if (rMax != null) {
        chips.push({ label: `${t('search.upTo', 'up to')} ${rMax} ${t('listingCard.roomMany', 'rooms')}`, category: 'rooms', onRemove: removeRooms })
      }

      // Price
      const removePrice = () => commitParsedWithout(() => ({ price_min: undefined, price_max: undefined }))
      if (pc.price_min != null && pc.price_max != null) {
        chips.push({ label: `${fmt(pc.price_min)}\u2013${fmt(pc.price_max)} CZK`, category: 'price', onRemove: removePrice })
      } else if (pc.price_max != null) {
        chips.push({ label: `max ${fmt(pc.price_max)} CZK`, category: 'price', onRemove: removePrice })
      } else if (pc.price_min != null) {
        chips.push({ label: `from ${fmt(pc.price_min)} CZK`, category: 'price', onRemove: removePrice })
      }

      // Area
      const removeArea = () => commitParsedWithout(() => ({ area_min: undefined, area_max: undefined }))
      if (pc.area_min != null && pc.area_max != null) {
        chips.push({ label: `${pc.area_min}\u2013${pc.area_max} m\u00B2`, category: 'area', onRemove: removeArea })
      } else if (pc.area_min != null) {
        chips.push({ label: `min ${pc.area_min} m\u00B2`, category: 'area', onRemove: removeArea })
      } else if (pc.area_max != null) {
        chips.push({ label: `max ${pc.area_max} m\u00B2`, category: 'area', onRemove: removeArea })
      }

      // Floor
      const removeFloor = () => commitParsedWithout(() => ({ floor_min: undefined, floor_max: undefined }))
      if (pc.floor_min != null && pc.floor_max != null) {
        chips.push({ label: `floor ${pc.floor_min}\u2013${pc.floor_max}`, category: 'floor', onRemove: removeFloor })
      } else if (pc.floor_min != null) {
        chips.push({ label: `floor ${pc.floor_min}+`, category: 'floor', onRemove: removeFloor })
      } else if (pc.floor_max != null) {
        chips.push({ label: `up to floor ${pc.floor_max}`, category: 'floor', onRemove: removeFloor })
      }

      // City & districts
      if (pc.city) {
        const removeLocation = () => commitParsedWithout(() => ({ city: undefined, districts: undefined }))
        if (pc.districts && pc.districts.length > 0) {
          chips.push({ label: pc.districts.join(', '), category: 'location', onRemove: removeLocation })
        } else {
          chips.push({ label: pc.city, category: 'location', onRemove: removeLocation })
        }
      }

      // Transaction type — always shown (like the city chip), not just when non-default,
      // since rent/sale is a primary selector rather than an optional refinement.
      {
        const tType = pc.transaction_type === 'sale' ? 'sale' : 'rent'
        chips.push({
          label: t(`search.${tType}`, tType),
          category: 'type',
          onRemove: () => commitParsedWithout(() => ({ transaction_type: 'rent' })),
        })
      }
      if (pc.type && pc.type !== 'apartment') {
        chips.push({ label: pc.type, category: 'type', onRemove: () => commitParsedWithout(() => ({ type: undefined })) })
      }

      // Keywords
      pc.keywords.forEach((kw) => {
        const tag = allTags.find((t) => t.id === kw)
        if (tag) {
          const kwDef = keywordDefMap.get(kw)
          const resolvedIcon = kwDef?.icon ? getIconComponent(kwDef.icon) : undefined
          chips.push({
            label: tag.label,
            category: 'feature',
            icon: resolvedIcon,
            onRemove: () => commitParsedWithout((prev) => ({ keywords: prev.keywords.filter((k) => k !== kw) })),
          })
        }
      })

    } else {
      // FILTER MODE: build chips from filter state
      const removeRooms = () => { chipRemovedRef.current = true; updateFilter('rooms_min', undefined); updateFilter('rooms_max', undefined) }
      const rMin = filters.rooms_min
      const rMax = filters.rooms_max
      if (rMin != null && rMax != null) {
        const label = rMin === rMax ? `${rMin} room${rMin !== 1 ? 's' : ''}` : `${rMin}\u2013${rMax} rooms`
        chips.push({ label, category: 'rooms', onRemove: removeRooms })
      } else if (rMin != null) {
        chips.push({ label: `${rMin}+ rooms`, category: 'rooms', onRemove: removeRooms })
      } else if (rMax != null) {
        chips.push({ label: `up to ${rMax} rooms`, category: 'rooms', onRemove: removeRooms })
      }

      // Price
      const removePrice = () => { chipRemovedRef.current = true; updateFilter('price_min', undefined); updateFilter('price_max', undefined) }
      const priceSuffix = filters.use_total_price !== false ? ' est. total' : ''
      if (filters.price_min != null && filters.price_max != null) {
        chips.push({ label: `${fmt(filters.price_min)}\u2013${fmt(filters.price_max)} CZK${priceSuffix}`, category: 'price', onRemove: removePrice })
      } else if (filters.price_max != null) {
        chips.push({ label: `max ${fmt(filters.price_max)} CZK${priceSuffix}`, category: 'price', onRemove: removePrice })
      } else if (filters.price_min != null) {
        chips.push({ label: `from ${fmt(filters.price_min)} CZK${priceSuffix}`, category: 'price', onRemove: removePrice })
      }

      // Area
      const removeArea = () => { chipRemovedRef.current = true; updateFilter('area_min', undefined); updateFilter('area_max', undefined) }
      if (filters.area_min != null && filters.area_max != null) {
        chips.push({ label: `${filters.area_min}\u2013${filters.area_max} m\u00B2`, category: 'area', onRemove: removeArea })
      } else if (filters.area_min != null) {
        chips.push({ label: `min ${filters.area_min} m\u00B2`, category: 'area', onRemove: removeArea })
      } else if (filters.area_max != null) {
        chips.push({ label: `max ${filters.area_max} m\u00B2`, category: 'area', onRemove: removeArea })
      }

      // Floor
      const removeFloor = () => { chipRemovedRef.current = true; updateFilter('floor_min', undefined); updateFilter('floor_max', undefined) }
      if (filters.floor_min != null && filters.floor_max != null) {
        chips.push({ label: `floor ${filters.floor_min}\u2013${filters.floor_max}`, category: 'floor', onRemove: removeFloor })
      } else if (filters.floor_min != null) {
        chips.push({ label: `floor ${filters.floor_min}+`, category: 'floor', onRemove: removeFloor })
      } else if (filters.floor_max != null) {
        chips.push({ label: `up to floor ${filters.floor_max}`, category: 'floor', onRemove: removeFloor })
      }

      // City & districts
      if (selectedCity) {
        const removeLocation = () => { chipRemovedRef.current = true; setSelectedCity(''); setSelectedDistricts([]) }
        if (selectedDistricts.length > 0) {
          chips.push({ label: selectedDistricts.join(', '), category: 'location', onRemove: removeLocation })
        } else {
          chips.push({ label: selectedCity, category: 'location', onRemove: removeLocation })
        }
      }

      // Transaction type — always shown (like the city chip), not just when non-default,
      // since rent/sale is a primary selector rather than an optional refinement.
      {
        const tType = filters.transaction_type === 'sale' ? 'sale' : 'rent'
        chips.push({
          label: t(`search.${tType}`, tType),
          category: 'type',
          onRemove: () => { chipRemovedRef.current = true; updateFilter('transaction_type', 'rent') },
        })
      }
      if (filters.type && filters.type !== 'apartment') {
        chips.push({ label: filters.type, category: 'type', onRemove: () => { chipRemovedRef.current = true; updateFilter('type', 'apartment') } })
      }

      // Active tags
      activeTags.forEach((tagId) => {
        const tag = allTags.find((t) => t.id === tagId)
        if (tag && !['rooms', 'price', 'area'].includes(tag.category)) {
          const kwDef = keywordDefMap.get(tagId)
          const resolvedIcon = kwDef?.icon ? getIconComponent(kwDef.icon) : undefined
          chips.push({
            label: tag.label,
            category: 'feature',
            icon: resolvedIcon,
            onRemove: () => { chipRemovedRef.current = true; removeTag(tagId) },
          })
        }
      })
    }

    // Travel time (always from state, regardless of mode)
    if (travelTime) {
      chips.push({
        label: `${travelTime.minutes} min ${travelTime.mode} from ${travelTime.origin}`,
        category: 'travel',
        onRemove: () => { chipRemovedRef.current = true; setTravelTime(null) },
      })
    }

    return chips
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedCriteria, filters, activeTags, selectedCity, selectedDistricts, travelTime, updateFilter, removeTag, setTravelTime, setSelectedCity, setSelectedDistricts, clearTags, addTag, setQuery])

  // Sync URL params to store on mount
  React.useEffect(() => {
    const q = searchParams.get('q')
    const transactionType = searchParams.get('transaction_type')
    const city = searchParams.get('city')
    const districts = searchParams.get('districts')
    if (q !== null) {
      setQuery(q)
    }
    if (transactionType === 'rent' || transactionType === 'sale') {
      updateFilter('transaction_type', transactionType)
    }
    if (city) {
      setSelectedCity(city)
    }
    if (districts) {
      setSelectedDistricts(districts.split(',').filter(Boolean))
    }
  }, [searchParams, setQuery, updateFilter, setSelectedCity, setSelectedDistricts])

  // Split dealbreaker IDs into keyword tags (for backend) vs static numeric filters (for client-side)
  // Static filter tags (like "1 room", "2+ rooms") map to numeric listing fields, not keywords
  const staticTagMap = React.useMemo(() => new Map(STATIC_FILTER_TAGS.map((t) => [t.id, t])), [])

  const splitDealbreakers = React.useCallback((ids: string[]) => {
    const keywordTags: string[] = []
    const numericFilters: typeof STATIC_FILTER_TAGS = []
    for (const id of ids) {
      const staticTag = staticTagMap.get(id)
      if (staticTag) {
        numericFilters.push(staticTag)
      } else {
        keywordTags.push(id)
      }
    }
    return { keywordTags, numericFilters }
  }, [staticTagMap])

  // Build search request from current state (used by handleSearch, load more, map view)
  const buildCurrentRequest = React.useCallback((overrides?: { limitOverride?: number; offsetOverride?: number }): SearchRequest => {
    const effectiveLimit = overrides?.limitOverride ?? limit
    const effectiveOffset = overrides?.offsetOverride ?? 0

    // Only merge keyword-based dealbreakers into tags (static ones are applied client-side)
    const { keywordTags: mustHaveKeywordTags } = splitDealbreakers(dealbreakers.mustHave || [])

    if (parsedCriteria) {
      // Text mode: build from parsedCriteria (LLM-parsed values)
      const mergedTags = [...new Set([...parsedCriteria.keywords, ...mustHaveKeywordTags])]
      return {
        query: query?.trim() || '',
        filters: {
          country,
          rooms_min: parsedCriteria.rooms_min,
          rooms_max: parsedCriteria.rooms_max,
          price_min: parsedCriteria.price_min,
          price_max: parsedCriteria.price_max,
          area_min: parsedCriteria.area_min,
          area_max: parsedCriteria.area_max,
          floor_min: parsedCriteria.floor_min,
          floor_max: parsedCriteria.floor_max,
          city: parsedCriteria.city,
          districts: parsedCriteria.districts,
          transaction_type: (parsedCriteria.transaction_type as 'rent' | 'sale') || filters.transaction_type || 'rent',
        },
        tags: mergedTags,
        sort,
        limit: effectiveLimit,
        offset: effectiveOffset,
      }
    }

    // Filter mode: build from store state
    const tagFilters = getFiltersFromTags()
    const mergedTags = [...new Set([...activeTags, ...mustHaveKeywordTags])]
    return {
      query: query?.trim() || '',
      filters: {
        ...filters,
        ...tagFilters,
        country,
        city: selectedCity,
        districts: selectedDistricts.length > 0 ? selectedDistricts : undefined,
        transaction_type: filters.transaction_type || 'rent',
      },
      tags: mergedTags,
      travel_time: travelTime || undefined,
      sort,
      limit: effectiveLimit,
      offset: effectiveOffset,
    }
  }, [parsedCriteria, query, filters, activeTags, country, selectedCity, selectedDistricts, travelTime, sort, limit, getFiltersFromTags, dealbreakers, splitDealbreakers])

  const buildSearchFingerprint = React.useCallback((): string => {
    const req = buildCurrentRequest({ limitOverride: 0, offsetOverride: 0 })
    const { limit: _l, offset: _o, ...criteriaOnly } = req
    return JSON.stringify(criteriaOnly)
  }, [buildCurrentRequest])

  // The criteria of the LAST SEARCH THAT RAN (set in handleSearch), as opposed
  // to the live store state the user may be mid-editing. The map's point cloud
  // and its invalidation effects hang off this — see handleSearch for why.
  const [committedCriteria, setCommittedCriteria] = React.useState<MapPointsRequest | null>(null)

  const totalPages = Math.max(1, Math.ceil(totalCount / limit))



  // Value-anchored deep pagination: on demand, sample the sort key at the
  // 0/25/50/75/100% positions (five limit=1 fetches) and offer them as jump
  // chips ("21 500 Kč · p. 132"). Bare page numbers give no reason to jump
  // deep; the sort key's value does.
  const jumpKind = SORT_JUMP_KIND[sort]
  // Stored as absolute offsets (view-agnostic) so both the grid (limit-sized
  // pages) and the map (100-sized pages) can map them onto their own pages.
  const [pageWaypoints, setPageWaypoints] = React.useState<Array<{ offset: number; label: string }> | null>(null)
  const [waypointsLoading, setWaypointsLoading] = React.useState(false)

  const waypointLabel = React.useCallback(
    (l: Listing): string => {
      switch (jumpKind) {
        case 'price':
          return fmtCZK(l.price)
        case 'date':
          return l.scraped_at
            ? new Date(l.scraped_at).toLocaleDateString(uiLanguage === 'cs' ? 'cs-CZ' : 'en-GB', {
                day: 'numeric',
                month: 'short',
              })
            : '—'
        case 'area':
          return l.area != null ? `${l.area} m²` : '—'
        case 'rooms':
          return l.rooms != null ? `${l.rooms}+kk` : '—'
        default:
          return '—'
      }
    },
    [jumpKind, uiLanguage]
  )

  const loadPageWaypoints = React.useCallback(async () => {
    setWaypointsLoading(true)
    const fingerprint = buildSearchFingerprint()
    try {
      const offsets = [...new Set(
        [0, 0.25, 0.5, 0.75, 1].map((q) => Math.max(0, Math.round(q * (totalCount - 1))))
      )]
      const responses = await Promise.all(
        offsets.map((off) => api.search(buildCurrentRequest({ limitOverride: 1, offsetOverride: off })))
      )
      if (buildSearchFingerprint() !== fingerprint) return // search changed mid-flight — discard
      const points: Array<{ offset: number; label: string }> = []
      responses.forEach((r, i) => {
        const listing = r.results[0]
        if (!listing) return
        points.push({ offset: offsets[i], label: waypointLabel(listing) })
      })
      setPageWaypoints(points)
    } catch (error) {
      console.error('Page waypoints error:', error)
      if (error instanceof ApiError && error.isRateLimited) {
        const limitInfo = error.rateLimitInfo
        setRateLimitInfo({
          retryAfter: error.retryAfter || 60,
          dayRemaining: limitInfo?.dayRemaining ?? 0,
          dayLimit: limitInfo?.dayLimit ?? 20,
        })
      }
    } finally {
      setWaypointsLoading(false)
    }
  }, [totalCount, buildCurrentRequest, buildSearchFingerprint, waypointLabel])

  const handleSearch = React.useCallback(async (source: 'text' | 'filters' | 'chip' | 'sort' | 'init') => {
    // Central gate for every search trigger (mount, header, command palette,
    // saved searches, sort/tag changes, etc.) — covers entry points that
    // navigate here directly instead of going through HomeSearchEntry's own
    // pre-navigation gate (e.g. bookmarked /search?q=... links).
    if (REQUIRE_AUTH_FOR_SEARCH && !user) {
      pendingAuthRetrySourceRef.current = source
      signIn()
      return
    }
    pendingAuthRetrySourceRef.current = null
    searchSourceRef.current = source
    // Back-navigation restore: the init search resumes at the URL's page
    // instead of page 1. User-driven searches start fresh. (Map view has no
    // page to restore any more — it loads every match at once.)
    const restorePage = source === 'init' ? pendingListPageRef.current : null
    pendingListPageRef.current = null
    const restoreOffset = restorePage ? (restorePage - 1) * limit : 0
    setCurrentPage(restorePage ?? 1)
    setPageWaypoints(null)
    setOffset(restoreOffset)
    setIsLoading(true)
    setHasSearched(true)
    setSearchError(null)
    setSearchedQuery(source === 'text' ? (query?.trim() || '') : '')

    // Pre-search state mutations based on source
    if (source === 'text') {
      // Text search: clear all filter UI state. LLM will determine everything from text.
      // Transaction type (rent/sale) is a standalone toggle in the search bar, not part
      // of the LLM-parsed filter set, so it must survive this reset — otherwise it
      // silently reverts to 'rent' whenever the query text is empty (see below).
      const transactionTypeToKeep = filters.transaction_type || 'rent'
      clearFilters() // resets filters to default + clears activeTags
      updateFilter('transaction_type', transactionTypeToKeep)
      setSelectedCity('Praha')
      setSelectedDistricts([])
      setTravelTime(null)
      setParsedCriteria(null)
    } else if (source === 'filters') {
      // Filter search: clear text input and parsed criteria
      setQuery('')
      setParsedCriteria(null)
    }

    try {
      let searchRequest: SearchRequest

      // Only send keyword-based must-have dealbreakers as tags to backend
      // Static filter tags (rooms, price, area) are applied client-side after results
      const { keywordTags: mustHaveKeywordTags } = splitDealbreakers(dealbreakers.mustHave || [])
      
      if (source === 'text') {
        // Text search: send query with minimal filters (let LLM parse everything)
        // Always include the selected city as a baseline; LLM-parsed city from the query
        // can still override it on the backend.
        searchRequest = {
          query: query?.trim() || '',
          filters: { country, city: selectedCity, transaction_type: filters.transaction_type || 'rent' },
          tags: [...mustHaveKeywordTags],
          sort,
          limit,
          offset: 0,
        }
      } else if (source === 'filters') {
        // Filter search: send filters with empty query
        const tagFilters = getFiltersFromTags()
        const mergedTags = [...new Set([...activeTags, ...mustHaveKeywordTags])]
        searchRequest = {
          query: '',
          filters: {
            ...filters,
            ...tagFilters,
            country,
            city: selectedCity,
            districts: selectedDistricts.length > 0 ? selectedDistricts : undefined,
            transaction_type: filters.transaction_type || 'rent',
          },
          tags: mergedTags,
          travel_time: travelTime || undefined,
          sort,
          limit,
          offset: 0,
        }
      } else {
        // chip / sort / init: use current state via buildCurrentRequest.
        searchRequest = buildCurrentRequest(restoreOffset ? { offsetOverride: restoreOffset } : undefined)
      }

      // Record the criteria this search actually ran. The map's point cloud
      // is keyed off THIS, not off live store state: the search box writes
      // `query` on every keystroke, and keying the map off live state made it
      // refetch — LLM parse, geocode, isochrone, the lot — per character
      // typed, while the grid only searches on Search/Enter (user 2026-08-17:
      // "map auto-searches; list doesn't" + "map takes 10s+"). Set before the
      // await so the cloud loads alongside the grid, not after it.
      setCommittedCriteria({
        query: searchRequest.query || undefined,
        filters: searchRequest.filters,
        tags: searchRequest.tags,
        travel_time: searchRequest.travel_time,
      })

      const response = await api.search(searchRequest)
      
      // Apply client-side dealbreaker filters from settings
      // Static filter tags (rooms, price, area) need numeric field checks;
      // keyword tags are checked against listing.keywords
      let filteredResults = response.results

      // Helper: does a dealbreaker ID match a listing?
      const dealbreakerMatchesListing = (id: string, listing: typeof response.results[0]) => {
        const staticTag = staticTagMap.get(id)
        if (staticTag) {
          const { field, value } = staticTag.value
          // Map filter field names to listing property names
          const fieldToProperty: Record<string, keyof typeof listing> = {
            rooms_exact: 'rooms', rooms_min: 'rooms', rooms_max: 'rooms',
            price_min: 'price', price_max: 'price',
            area_min: 'area', area_max: 'area',
            floor_min: 'floor', floor_max: 'floor',
          }
          const listingVal = listing[fieldToProperty[field] || field as keyof typeof listing] as number | undefined
          if (listingVal == null) return false
          if (field === 'rooms_exact') return listingVal === value
          if (field.endsWith('_min')) return listingVal >= value
          if (field.endsWith('_max')) return listingVal <= value
          return false
        }
        return listing.keywords?.includes(id)
      }

      // Must-have: keep only listings matching ALL must-have dealbreakers
      const { numericFilters: mustHaveNumeric } = splitDealbreakers(dealbreakers.mustHave || [])
      if (mustHaveNumeric.length > 0) {
        filteredResults = filteredResults.filter((listing) =>
          mustHaveNumeric.every((tag) => dealbreakerMatchesListing(tag.id, listing))
        )
      }

      if (listingFreshness) {
        const cutoffDate = new Date()
        cutoffDate.setDate(cutoffDate.getDate() - listingFreshness)
        filteredResults = filteredResults.filter((listing) => {
          if (!listing.scraped_at) return true // keep listings without date
          return new Date(listing.scraped_at) >= cutoffDate
        })
      }

      // One-shot "Show Only New" filter from saved search panel
      if (newSinceRef.current) {
        const cutoff = new Date(newSinceRef.current)
        filteredResults = filteredResults.filter((listing) => {
          if (!listing.scraped_at) return false
          return new Date(listing.scraped_at) >= cutoff
        })
        newSinceRef.current = null // one-shot: clear after use
      }

      if (notificationListingIdsRef.current && notificationListingIdsRef.current.length > 0) {
        const wantedIds = new Set(notificationListingIdsRef.current)
        filteredResults = filteredResults.filter((listing) => wantedIds.has(listing.id))
        notificationListingIdsRef.current = null // one-shot: clear after use
      }

      setResults(filteredResults)
      setTotalCount(filteredResults.length < response.results.length ? filteredResults.length : response.total_count)
      setReferenceLocation(response.reference_location || null)

      // Post-response: store parsed criteria for text search and sync to filter UI
      if (source === 'text' && response.query) {
        const pf = response.parsed_filters || {}
        setParsedCriteria({
          rooms_min: pf.rooms_min ?? (pf.rooms_exact != null ? pf.rooms_exact : undefined),
          rooms_max: pf.rooms_max ?? (pf.rooms_exact != null ? pf.rooms_exact : undefined),
          price_min: pf.price_min,
          price_max: pf.price_max,
          area_min: pf.area_min,
          area_max: pf.area_max,
          floor_min: pf.floor_min,
          floor_max: pf.floor_max,
          city: pf.city,
          districts: pf.districts?.length ? pf.districts : pf.district ? [pf.district] : undefined,
          type: pf.type,
          transaction_type: (pf as Record<string, unknown>).transaction_type as string | undefined,
          keywords: response.parsed_keywords || [],
          semanticQuery: response.semantic_query || null,
        })

        // Sync parsed criteria to filter UI state so filters reflect the parsed chips
        updateFilter('rooms_min', pf.rooms_min ?? (pf.rooms_exact != null ? pf.rooms_exact : undefined))
        updateFilter('rooms_max', pf.rooms_max ?? (pf.rooms_exact != null ? pf.rooms_exact : undefined))
        updateFilter('price_min', pf.price_min)
        updateFilter('price_max', pf.price_max)
        updateFilter('area_min', pf.area_min)
        updateFilter('area_max', pf.area_max)
        updateFilter('floor_min', pf.floor_min)
        updateFilter('floor_max', pf.floor_max)
        if (pf.city) setSelectedCity(pf.city)
        if (pf.districts?.length) setSelectedDistricts(pf.districts)
        else if (pf.district) setSelectedDistricts([pf.district])
        if (pf.type) updateFilter('type', pf.type as 'apartment' | 'room' | 'house')
        if ((pf as Record<string, unknown>).transaction_type === 'rent' || (pf as Record<string, unknown>).transaction_type === 'sale') {
          updateFilter('transaction_type', (pf as Record<string, unknown>).transaction_type as 'rent' | 'sale')
        }
        clearTags()
        ;(response.parsed_keywords || []).forEach((kw: string) => addTag(kw))
      }

      // For init source with a query, also store parsed criteria and sync to filter UI
      if (source === 'init' && response.query && response.parsed_filters) {
        const pf = response.parsed_filters
        setParsedCriteria({
          rooms_min: pf.rooms_min ?? (pf.rooms_exact != null ? pf.rooms_exact : undefined),
          rooms_max: pf.rooms_max ?? (pf.rooms_exact != null ? pf.rooms_exact : undefined),
          price_min: pf.price_min,
          price_max: pf.price_max,
          area_min: pf.area_min,
          area_max: pf.area_max,
          floor_min: pf.floor_min,
          floor_max: pf.floor_max,
          city: pf.city,
          districts: pf.districts?.length ? pf.districts : pf.district ? [pf.district] : undefined,
          type: pf.type,
          transaction_type: (pf as Record<string, unknown>).transaction_type as string | undefined,
          keywords: response.parsed_keywords || [],
          semanticQuery: response.semantic_query || null,
        })

        // Sync parsed criteria to filter UI state so filters reflect the parsed chips
        updateFilter('rooms_min', pf.rooms_min ?? (pf.rooms_exact != null ? pf.rooms_exact : undefined))
        updateFilter('rooms_max', pf.rooms_max ?? (pf.rooms_exact != null ? pf.rooms_exact : undefined))
        updateFilter('price_min', pf.price_min)
        updateFilter('price_max', pf.price_max)
        updateFilter('area_min', pf.area_min)
        updateFilter('area_max', pf.area_max)
        updateFilter('floor_min', pf.floor_min)
        updateFilter('floor_max', pf.floor_max)
        if (pf.city) setSelectedCity(pf.city)
        if (pf.districts?.length) setSelectedDistricts(pf.districts)
        else if (pf.district) setSelectedDistricts([pf.district])
        if (pf.type) updateFilter('type', pf.type as 'apartment' | 'room' | 'house')
        if ((pf as Record<string, unknown>).transaction_type === 'rent' || (pf as Record<string, unknown>).transaction_type === 'sale') {
          updateFilter('transaction_type', (pf as Record<string, unknown>).transaction_type as 'rent' | 'sale')
        }
        clearTags()
        ;(response.parsed_keywords || []).forEach((kw: string) => addTag(kw))
      }

      // Set highlighted keywords for listing card highlighting
      // Always include dealbreaker must-have keywords so they're highlighted on every card
      const mustHaveKeywords = dealbreakers.mustHave || []
      if (source === 'text') {
        // Text mode: highlight keywords from LLM response + dealbreakers
        setHighlightedKeywords([...new Set([...(response.parsed_keywords || []), ...mustHaveKeywords])])
      } else if (source === 'init') {
        // Init mode: if there's a text query, use parsed keywords; otherwise use active tags
        const keywordsToHighlight = response.query ? (response.parsed_keywords || []) : activeTags
        setHighlightedKeywords([...new Set([...keywordsToHighlight, ...mustHaveKeywords])])
      } else if (source === 'filters') {
        // Filter mode: highlight the explicitly selected tags + dealbreakers
        setHighlightedKeywords([...new Set([...activeTags, ...mustHaveKeywords])])
      } else if (source === 'chip') {
        // Chip removal: highlight the remaining active tags + dealbreakers
        setHighlightedKeywords([...new Set([...activeTags, ...mustHaveKeywords])])
      }
      // sort: keep existing highlighted keywords (no change needed)

      // Update URL
      const params = new URLSearchParams()
      if (query && (source === 'text' || source === 'init' || source === 'sort' || source === 'chip')) {
        params.set('q', query)
      }
      // transaction_type is a standalone toggle that applies regardless of search
      // source (including text search), so keep it in sync with the URL always —
      // otherwise it's lost on the next page load / pagination request.
      if (filters.transaction_type && filters.transaction_type !== 'rent') params.set('transaction_type', filters.transaction_type)
      if (source !== 'text') {
        if (selectedCity && selectedCity !== 'Praha') params.set('city', selectedCity)
        if (selectedDistricts.length > 0) params.set('districts', selectedDistricts.join(','))
      }

      params.delete('page')
      const newURL = `/search${params.toString() ? `?${params.toString()}` : ''}`
      window.history.replaceState({}, '', newURL)

      // Record to recent search history for text, filter, and init (home page redirect) searches
      const hasQuery = !!query?.trim()
      const hasFilters = activeTags.length > 0 || selectedDistricts.length > 0 || !!travelTime ||
        Object.keys(filters).some(k => k !== 'country' && filters[k as keyof typeof filters] != null)
      const shouldRecord = searchHistoryEnabled && (source === 'text' || source === 'filters' || (source === 'init' && (hasQuery || hasFilters))) && user
      if (shouldRecord) {
        try {
          // For 'init', determine type by whether there's a text query or filter state
          const recordType = (source === 'filters' || (source === 'init' && !hasQuery && hasFilters)) ? 'filters' : 'text'
          const recentEntry = createRecentSearch({
            type: recordType,
            query: recordType === 'text' ? query?.trim() : undefined,
            filters: recordType === 'filters' ? { ...filters, country, city: selectedCity } : undefined,
            tags: recordType === 'filters' ? activeTags : undefined,
            city: selectedCity,
            districts: selectedDistricts,
            travelTime: travelTime,
            sort,
          })
          addRecentSearch(recentEntry)
          // Sync to Firestore in the background
          addRecentSearchFirestore(user.uid, recentEntry).catch(console.error)
        } catch (e) {
          console.error('Failed to record recent search:', e)
        }
      }
    } catch (error) {
      console.error('Search error:', error)

      if (error instanceof ApiError) {
        if (error.isRateLimited) {
          const limitInfo = error.rateLimitInfo
          setRateLimitInfo({
            retryAfter: error.retryAfter || 60,
            dayRemaining: limitInfo?.dayRemaining ?? 0,
            dayLimit: limitInfo?.dayLimit ?? 20,
          })
          setSearchError(null)
        } else if (error.isUnauthorized) {
          setSearchError(t('search.signInToContinue', 'Please sign in to continue searching.'))
        } else {
          setSearchError(error.message || t('search.genericError', 'Something went wrong. Please try again.'))
        }
      } else {
        setSearchError(t('search.connectionError', 'Unable to connect to search. Please check your connection and try again.'))
      }

      setResults([])
      setTotalCount(0)
    } finally {
      setIsLoading(false)
    }
  }, [query, filters, activeTags, country, selectedCity, selectedDistricts, travelTime, sort, limit, setResults, setTotalCount, setIsLoading, setHasSearched, getFiltersFromTags, clearFilters, setQuery, setSelectedCity, setSelectedDistricts, setTravelTime, setHighlightedKeywords, setReferenceLocation, buildCurrentRequest, buildSearchFingerprint, user, signIn, addRecentSearch, dealbreakers, listingFreshness, searchHistoryEnabled, setOffset])

  // Trigger initial search on mount
  React.useEffect(() => {
    if (!hasInitialSearched.current) {
      // If URL has a query param but the store hasn't synced it yet (stale closure),
      // defer the initial search until the next render when query is up-to-date.
      const urlQ = searchParams.get('q')
      if (urlQ && query !== urlQ) {
        return
      }
      // Check for newSince stored by navigation from another page
      try {
        const storedNewSince = sessionStorage.getItem('roostr:newSince')
        if (storedNewSince) {
          newSinceRef.current = storedNewSince
          sessionStorage.removeItem('roostr:newSince')
        }
        const storedNotificationIds = sessionStorage.getItem('roostr:notificationListingIds')
        if (storedNotificationIds) {
          const parsed = JSON.parse(storedNotificationIds)
          if (Array.isArray(parsed)) {
            notificationListingIdsRef.current = parsed.filter((id): id is string => typeof id === 'string')
          }
          sessionStorage.removeItem('roostr:notificationListingIds')
        }
      } catch {}
      hasInitialSearched.current = true
      handleSearch('init')
    }
  }, [handleSearch, query, searchParams])

  // Retry the search that REQUIRE_AUTH_FOR_SEARCH blocked, once the sign-in modal
  // it opened succeeds (user goes from null -> a real user).
  React.useEffect(() => {
    if (REQUIRE_AUTH_FOR_SEARCH && user && pendingAuthRetrySourceRef.current) {
      const retrySource = pendingAuthRetrySourceRef.current
      pendingAuthRetrySourceRef.current = null
      handleSearch(retrySource)
    }
  }, [user, handleSearch])

  // Re-search when sort changes (after initial search)
  // Note: activeTags changes no longer auto-trigger search; tags take effect
  // only through explicit "Show results" (filter path) or chip removal.
  const prevSortRef = React.useRef(sort)
  React.useEffect(() => {
    if (prevSortRef.current !== sort && hasSearched && hasInitialSearched.current) {
      prevSortRef.current = sort
      handleSearch('sort')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort])

  // Re-search after a filter chip is removed or tag is added from listing card
  React.useEffect(() => {
    if ((chipRemovedRef.current || tagAddedRef.current) && hasSearched && hasInitialSearched.current) {
      const wasTagAdded = tagAddedRef.current
      chipRemovedRef.current = false
      tagAddedRef.current = false
      // When a tag is added from a listing card, treat it as a filter search
      // so the query is cleared and chips are derived from filter state
      handleSearch(wasTagAdded ? 'filters' : 'chip')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeTags, selectedCity, selectedDistricts, travelTime, parsedCriteria])

  // Listen for header search events (carries source in event detail)
  React.useEffect(() => {
    const handleHeaderSearch = (e: Event) => {
      const detail = (e as CustomEvent).detail
      const source = detail?.source === 'filters' ? 'filters' : 'text'
      handleSearch(source)
    }

    window.addEventListener('header-search-triggered', handleHeaderSearch)
    return () => {
      window.removeEventListener('header-search-triggered', handleHeaderSearch)
    }
  }, [handleSearch])

  // Flag to trigger search after saved search is loaded and state updates
  const [savedSearchTrigger, setSavedSearchTrigger] = React.useState(0)
  const prevSavedSearchTriggerRef = React.useRef(0)

  // Listen for saved search loaded event
  React.useEffect(() => {
    const handleSavedSearchLoaded = (e: Event) => {
      const detail = (e as CustomEvent).detail
      newSinceRef.current = detail?.newSince || null
      notificationListingIdsRef.current = Array.isArray(detail?.listingIds) ? detail.listingIds : null
      // Increment trigger to force re-search after state updates
      setSavedSearchTrigger(prev => prev + 1)
    }

    window.addEventListener('saved-search-loaded', handleSavedSearchLoaded)
    return () => {
      window.removeEventListener('saved-search-loaded', handleSavedSearchLoaded)
    }
  }, [])

  // Trigger search when saved search is loaded (after state updates are applied)
  // Uses prevRef to only fire when savedSearchTrigger actually increments,
  // not when other deps (handleSearch, filters, etc.) change — which would cause an infinite loop.
  React.useEffect(() => {
    if (savedSearchTrigger > prevSavedSearchTriggerRef.current && hasInitialSearched.current) {
      prevSavedSearchTriggerRef.current = savedSearchTrigger
      handleSearch('init')
    }
  }, [savedSearchTrigger, handleSearch, filters, activeTags, query])

  // Listen for tag added from listing card
  React.useEffect(() => {
    const handleTagAdded = () => {
      tagAddedRef.current = true
    }

    window.addEventListener('tag-added', handleTagAdded)
    return () => {
      window.removeEventListener('tag-added', handleTagAdded)
    }
  }, [])

  // Listen for open save search dialog event
  React.useEffect(() => {
    const handleOpenSaveDialog = () => {
      if (user) {
        setSaveDialogOpen(true)
      } else {
        signIn()
      }
    }

    window.addEventListener('open-save-search-dialog', handleOpenSaveDialog)
    return () => {
      window.removeEventListener('open-save-search-dialog', handleOpenSaveDialog)
    }
  }, [user, signIn])

  // Re-run after something wrote the search store while we are already on this
  // route. Navigating to /search from /search does not remount, so the mount
  // effect above never fires — the command palette's "recent / saved search"
  // items would silently do nothing without this.
  React.useEffect(() => {
    const rerun = () => handleSearch('init')
    window.addEventListener(RERUN_SEARCH_EVENT, rerun)
    return () => window.removeEventListener(RERUN_SEARCH_EVENT, rerun)
  }, [handleSearch])

  // Track previous view mode to detect switches
  const prevViewModeRef = React.useRef(viewMode)

  // ── Map view: one point cloud, no pages ───────────────────────────────────
  // The map used to page through results 100 at a time, which users read as
  // arbitrary ("page 3 of 13" means nothing on a map, and no other property
  // site does it). Instead it now loads EVERY match's coordinates once per
  // search — a skinny id/lat/lon/price payload — and clusters them
  // client-side, so zooming out summarises and zooming in reveals, with no
  // network traffic in between.
  const [mapPoints, setMapPoints] = React.useState<MapPoint[]>([])
  const [mapPointsLoading, setMapPointsLoading] = React.useState(false)
  // Matches the search found but that have NO coordinates — the honest
  // difference between "N results" and "M on map". Reported by the map-points
  // endpoint (its `total` is counted BEFORE the geo requirement).
  const [mapUnmappable, setMapUnmappable] = React.useState(0)
  // Listings hydrated for whatever is currently in view — the side panel
  // renders real cards, and a clicked pin needs the full record.
  const [mapHydrated, setMapHydrated] = React.useState<Map<string, Listing>>(new Map())
  const [mapVisibleIds, setMapVisibleIds] = React.useState<string[]>([])
  const [mapInViewCount, setMapInViewCount] = React.useState(0)
  const mapPointsReqRef = React.useRef(0)
  const hydrateReqRef = React.useRef(0)

  // Fetch the cloud whenever the map is showing and the criteria change.
  // Keyed off the same fingerprint the grid uses, so map and grid always
  // describe the same match set.
  // Keyed off the COMMITTED criteria (what handleSearch last ran), never live
  // store state — the search box mutates live state per keystroke, and that
  // used to refetch the whole cloud on every character typed in map view.
  const mapFingerprint =
    hasSearched && viewMode === 'map' && committedCriteria ? JSON.stringify(committedCriteria) : null
  React.useEffect(() => {
    if (!mapFingerprint || !committedCriteria) return
    const seq = ++mapPointsReqRef.current
    setMapPointsLoading(true)
    api
      .getMapPoints(committedCriteria)
      .then((res) => {
        if (seq !== mapPointsReqRef.current) return // a newer search won
        setMapPoints(res.points)
        setMapUnmappable(Math.max(0, res.total - res.points.length))
        if (res.truncated) {
          console.warn('[map] point cloud truncated at the safety ceiling')
        }
      })
      .catch((err) => {
        if (seq !== mapPointsReqRef.current) return
        console.error('[map] failed to load points:', err)
        setMapPoints([])
        setMapUnmappable(0)
      })
      .finally(() => {
        if (seq === mapPointsReqRef.current) setMapPointsLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapFingerprint])

  // The map reports what's in the viewport on every idle; hydrate those ids so
  // the side panel can show real cards for exactly what the user is looking at.
  const handleMapViewport = React.useCallback(
    (ids: string[], countInView: number) => {
      setMapVisibleIds(ids)
      setMapInViewCount(countInView)
      const missing = ids.filter((id) => !mapHydrated.has(id))
      if (missing.length === 0) return
      const seq = ++hydrateReqRef.current
      api
        .getListings(missing)
        .then((listings) => {
          if (seq !== hydrateReqRef.current) return
          setMapHydrated((prev) => {
            const next = new Map(prev)
            listings.forEach((l) => next.set(l.id, l))
            return next
          })
        })
        .catch((err) => console.error('[map] hydrate failed:', err))
    },
    [mapHydrated]
  )

  // Hovering/selecting a pin can target a listing outside the viewport
  // hydration cap — fetch that one on demand so the mini-card is never a
  // shell.
  React.useEffect(() => {
    const id = selectedMapListingId ?? hoveredMapListingId
    if (!id || mapHydrated.has(id)) return
    let alive = true
    api
      .getListings([id])
      .then((ls) => {
        if (!alive || ls.length === 0) return
        setMapHydrated((prev) => {
          if (prev.has(id)) return prev
          const next = new Map(prev)
          ls.forEach((l) => next.set(l.id, l))
          return next
        })
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [selectedMapListingId, hoveredMapListingId, mapHydrated])

  // A new search invalidates both the cloud and anything hydrated from it.
  React.useEffect(() => {
    setMapHydrated(new Map())
    setMapVisibleIds([])
  }, [mapFingerprint])

  // Side-panel listings, in the map's viewport order.
  const mapVisibleListings = React.useMemo(
    () => mapVisibleIds.map((id) => mapHydrated.get(id)).filter((l): l is Listing => !!l),
    [mapVisibleIds, mapHydrated]
  )

  // ── District focus ("Jump to district") ────────────────────────────────────
  // Real per-district counts come from the FULL point cloud — every map point
  // carries `district` — not from the ≤60 hydrated cards the panel happens to
  // show (user 2026-08-16: "Praha 9 · 5 homes" was 5 of a 60-card sample, not
  // the district's size). Picking a district switches the panel into a
  // district mode that lists ALL of that district's listings (paged, hydrated
  // by id from the cloud) and fits the map to them.
  const districtIndex = React.useMemo(() => {
    const m = new Map<string, MapPoint[]>()
    for (const p of mapPoints) {
      const d = p.district || 'Praha'
      const arr = m.get(d)
      if (arr) arr.push(p)
      else m.set(d, [p])
    }
    return m
  }, [mapPoints])

  const DISTRICT_PAGE = 60
  const [focusDistrict, setFocusDistrict] = React.useState<string | null>(null)
  const [districtShown, setDistrictShown] = React.useState(DISTRICT_PAGE)
  const [districtLoading, setDistrictLoading] = React.useState(false)
  const districtReqRef = React.useRef(0)

  // Hydrate the visible slice of the focused district (grows with "show more").
  React.useEffect(() => {
    if (!focusDistrict) return
    const pts = districtIndex.get(focusDistrict) ?? []
    const wanted = pts.slice(0, districtShown).map((p) => p.id)
    const missing = wanted.filter((id) => !mapHydrated.has(id))
    if (missing.length === 0) return
    const seq = ++districtReqRef.current
    setDistrictLoading(true)
    api
      .getListings(missing)
      .then((ls) => {
        if (seq !== districtReqRef.current) return
        setMapHydrated((prev) => {
          const next = new Map(prev)
          ls.forEach((l) => next.set(l.id, l))
          return next
        })
      })
      .catch((err) => console.error('[map] district hydrate failed:', err))
      .finally(() => {
        if (seq === districtReqRef.current) setDistrictLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusDistrict, districtShown, districtIndex])

  const districtListings = React.useMemo(() => {
    if (!focusDistrict) return null
    const pts = districtIndex.get(focusDistrict) ?? []
    return pts
      .slice(0, districtShown)
      .map((p) => mapHydrated.get(p.id))
      .filter((l): l is Listing => !!l)
  }, [focusDistrict, districtShown, districtIndex, mapHydrated])

  const districtTotal = focusDistrict ? (districtIndex.get(focusDistrict)?.length ?? 0) : 0
  const districtCountsMap = React.useMemo(() => {
    const m = new Map<string, number>()
    districtIndex.forEach((pts, d) => m.set(d, pts.length))
    return m
  }, [districtIndex])
  const districtFitPoints = React.useMemo(
    () => (focusDistrict ? (districtIndex.get(focusDistrict) ?? null) : null),
    [focusDistrict, districtIndex]
  )

  // A new search resets district focus (its ids no longer exist).
  React.useEffect(() => {
    setFocusDistrict(null)
    setDistrictShown(DISTRICT_PAGE)
  }, [mapFingerprint])

  // Leaving district focus by the user's own map gesture: the panel goes back
  // to following the viewport as soon as they pan/zoom themselves.
  const handleMapViewportWithDistrict = React.useCallback(
    (ids: string[], countInView: number) => {
      handleMapViewport(ids, countInView)
      setFocusDistrict((cur) => (cur ? null : cur))
    },
    [handleMapViewport]
  )

  // Mirror view + page state into the URL (replaceState — no history spam) so
  // back/refresh restores it, and stash the URL for the listing-detail tab's
  // back button (details open in a new tab, which shares no in-memory state —
  // localStorage carries the way back).
  React.useEffect(() => {
    if (!hasSearched) return
    const params = new URLSearchParams(window.location.search)
    params.set('view', viewMode)
    params.delete('mpage') // map has no pages any more
    if (currentPage > 1) params.set('page', String(currentPage))
    else params.delete('page')
    const url = `/search?${params.toString()}`
    window.history.replaceState({}, '', url)
    try {
      localStorage.setItem('roostr:lastSearchUrl', url)
      // Shared "where Back should land" stash — /notifications writes it too;
      // the listing-detail back button reads whichever surface was last.
      localStorage.setItem('roostr:lastBrowseUrl', url)
    } catch {}
  }, [viewMode, currentPage, hasSearched])

  // (The old "load 100 more listings when switching to map" effect is gone:
  // the map no longer reads the shared results page at all — it has its own
  // point cloud — so switching views must not clobber the grid's results.)
  React.useEffect(() => {
    prevViewModeRef.current = viewMode
  }, [viewMode])


  const handlePageChange = React.useCallback(async (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === currentPage) return

    const newOffset = (nextPage - 1) * limit
    setIsLoading(true)
    setSearchError(null)

    try {
      const searchRequest = buildCurrentRequest({ offsetOverride: newOffset })
      const response = await api.search(searchRequest)

      // Keep dealbreaker/freshness behavior consistent with initial search.
      const dealbreakerMatchesListing = (id: string, listing: typeof response.results[0]) => {
        const staticTag = staticTagMap.get(id)
        if (staticTag) {
          const { field, value } = staticTag.value
          const fieldToProperty: Record<string, keyof typeof listing> = {
            rooms_exact: 'rooms', rooms_min: 'rooms', rooms_max: 'rooms',
            price_min: 'price', price_max: 'price',
            area_min: 'area', area_max: 'area',
            floor_min: 'floor', floor_max: 'floor',
          }
          const listingVal = listing[fieldToProperty[field] || field as keyof typeof listing] as number | undefined
          if (listingVal == null) return false
          if (field === 'rooms_exact') return listingVal === value
          if (field.endsWith('_min')) return listingVal >= value
          if (field.endsWith('_max')) return listingVal <= value
          return false
        }
        return listing.keywords?.includes(id)
      }

      let filteredResults = response.results
      const { numericFilters: mustHaveNumeric } = splitDealbreakers(dealbreakers.mustHave || [])
      if (mustHaveNumeric.length > 0) {
        filteredResults = filteredResults.filter((listing) =>
          mustHaveNumeric.every((tag) => dealbreakerMatchesListing(tag.id, listing))
        )
      }


      if (listingFreshness) {
        const cutoffDate = new Date()
        cutoffDate.setDate(cutoffDate.getDate() - listingFreshness)
        filteredResults = filteredResults.filter((listing) => {
          if (!listing.scraped_at) return true
          return new Date(listing.scraped_at) >= cutoffDate
        })
      }

      setResults(filteredResults)
      setTotalCount(response.total_count)
      setOffset(newOffset)
      setCurrentPage(nextPage)
      // URL + localStorage sync happens in the view/page mirror effect.
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      console.error('Pagination error:', error)
      if (error instanceof ApiError && error.isRateLimited) {
        const limitInfo = error.rateLimitInfo
        setRateLimitInfo({
          retryAfter: error.retryAfter || 60,
          dayRemaining: limitInfo?.dayRemaining ?? 0,
          dayLimit: limitInfo?.dayLimit ?? 20,
        })
      }
    } finally {
      setIsLoading(false)
    }
  }, [totalPages, currentPage, limit, buildCurrentRequest, staticTagMap, splitDealbreakers, dealbreakers.mustHave, listingFreshness, setResults, setTotalCount, setOffset, setIsLoading])

  // Map view pagination — fetches the requested batch of 100 and REPLACES the
  // results (mirrors list-view pagination instead of appending endlessly).

  return (
    // pb-24 below lg clears the floating List/Map pill — without it the pill
    // sat on top of the footer's social row at the bottom of the page.
    <div
      className={
        viewMode === 'map'
          ? // py-6 (not py-4) matches grid view's top offset; NO px here —
            // the header column below applies its own, and the map body wants
            // the full window width.
            'flex flex-col py-6 h-[calc(100dvh-64px)]'
          : 'container mx-auto px-4 py-6 pb-24 lg:pb-6'
      }
    >
      {/* Header column (count · save · sort · view toggle · chips · insights).
          Map view is deliberately full-bleed so the map spans the window, but
          that moved this whole block left and up every time the user toggled
          Grid↔Map. Re-applying `container mx-auto` here — and only here —
          pins the header to the SAME column as grid view while the map body
          below keeps its edge-to-edge width. */}
      <div className={viewMode === 'map' ? 'container mx-auto w-full px-4' : 'contents'}>
      {/* Error banner */}
      {searchError && !rateLimitInfo && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-destructive">{t('search.errorTitle', 'Search Error')}</p>
            <p className="text-sm text-destructive/80 mt-1">{searchError}</p>
          </div>
          <button
            onClick={() => setSearchError(null)}
            className="text-destructive/60 hover:text-destructive transition-colors"
            aria-label={t('search.dismissError', 'Dismiss error')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Results header — M3 Expressive toolbar (design search.jsx:417-497) */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="mr-auto text-[20px] font-semibold tracking-[-0.3px] text-foreground">
            {isLoading ? (
              t('search.searching', 'Searching...')
            ) : (
              <>
                <span className="serif-accent mr-1.5 text-[26px] text-primary">{totalCount.toLocaleString('cs-CZ')}</span>
                {t('search.apartmentsFound', 'apartments found')}
                {/* The query echo is hidden below sm: the header search pill
                    and the chip row below already show it, and on a phone it
                    was the thing pushing this headline onto a second line. */}
                {searchedQuery && (
                  <span className="hidden font-normal text-ink-muted sm:inline"> · &ldquo;{searchedQuery}&rdquo;</span>
                )}
              </>
            )}
          </h1>

          <button
            onClick={() => user ? setSaveDialogOpen(true) : signIn()}
            className="inline-flex items-center gap-1.5 rounded-full border border-outline bg-surface px-3.5 py-2 text-[13px] font-semibold text-foreground transition-transform active:scale-[0.97]"
            title={user ? t('search.saveThisSearch', 'Save this search') : t('search.signInToSaveSearches', 'Sign in to save searches')}
          >
            <Bookmark className="h-4 w-4 flex-shrink-0 text-ink-secondary" />
            <span className="hidden sm:inline whitespace-nowrap">{t('search.saveSearch', 'Save Search')}</span>
          </button>

          {hiddenListings.size > 0 && (
            <RMenu
              width={300}
              align="right"
              button={(open, toggle) => (
                <button
                  onClick={toggle}
                  title={t('search.hiddenListings', 'Hidden listings')}
                  className="inline-flex items-center gap-1.5 rounded-full border border-outline bg-surface py-2 pl-3.5 pr-2.5 text-[13px] font-semibold text-ink-secondary transition-transform active:scale-[0.97]"
                >
                  <EyeOff className="h-4 w-4" />
                  {hiddenListings.size} {t('search.hidden', 'hidden')}
                </button>
              )}
              items={[
                ...[...hiddenListings].map((id) => {
                  const hiddenListing = results.find((r) => r.id === id)
                  return {
                    icon: <Eye className="h-4 w-4" />,
                    // Hidden ids can outlive the current result set (they persist
                    // across queries) — never surface the raw id to the user.
                    label: hiddenListing?.title || t('search.hiddenListing', 'Hidden listing'),
                    sub: t('search.clickToUnhide', 'Click to unhide'),
                    onClick: () => showListing(id),
                  }
                }),
                {
                  icon: <RotateCcw className="h-4 w-4" />,
                  label: t('search.unhideAll', 'Unhide all'),
                  onClick: showAllHidden,
                },
              ]}
            />
          )}

          {viewMode === 'list' && (
            <div
              className="hidden items-center gap-2 rounded-full border border-outline bg-surface px-3.5 py-[7px] sm:flex"
              title={`${gridColumnsForCardSize(cardSize)} ${t('search.columns', 'columns')}`}
            >
              <Grip className="h-[15px] w-[15px] text-ink-muted" />
              <input
                type="range"
                min={0}
                max={GRID_COLUMN_STOPS.length - 1}
                step={1}
                value={gridStopIndexForCardSize(cardSize)}
                onChange={(e) => setCardSize(GRID_COLUMN_STOPS[Number(e.target.value)].size)}
                aria-label={t('search.gridColumns', 'Grid columns')}
                className="w-[92px] cursor-pointer accent-primary"
              />
              <LayoutGrid className="h-[18px] w-[18px] text-ink-secondary" />
            </div>
          )}

          {/* Below lg this moves into the chip row (it used to wrap onto a
              line of its own, costing ~56px of the first screen). */}
          <SortSelect className="hidden lg:inline-flex" onSort={() => handleSearch('sort')} />

          {/* Below lg the floating MobileViewToggle pill owns list/map
              switching — two toggles for one state confused the row. */}
          <div data-tour="view-toggle" className="hidden lg:block flex-none">
            <Seg
              value={viewMode}
              onChange={(v) => setViewMode(v)}
              options={[
                { value: 'list', label: t('search.grid', 'Grid'), icon: <LayoutGrid className="h-[17px] w-[17px]" /> },
                { value: 'map', label: t('search.map', 'Map'), icon: <MapIcon className="h-[17px] w-[17px]" /> },
              ]}
            />
          </div>
        </div>

        {/* ONE merged filter row (user-directed): quick pills + active chips
            share this container. Below lg the pills lead and the chips whose
            state a pill already shows (rooms/price/location) are hidden —
            active pills carry the value + their own ×. At lg the pills
            vanish (contents lg:hidden) and the full chips row returns; the
            container hides itself at lg when there are no chips. */}
            {/* The right-edge mask is the only "there's more →" cue this row
                has (measured: 692px of chips in a 375px viewport = 317px
                hidden, scrollbar-hide, no arrows). The extra pr below lg means
                that at maximum scroll the fade lands on padding rather than on
                the last chip. */}
            <div className={cn(
              'flex flex-nowrap overflow-x-auto scrollbar-hide -mx-4 px-4 pr-8 lg:mx-0 lg:px-0 lg:pr-0 lg:flex-wrap lg:overflow-visible items-center gap-2 mt-3.5',
              '[mask-image:linear-gradient(to_right,black_0,black_calc(100%-26px),transparent_100%)] lg:[mask-image:none]',
              !(parsedCriteria?.semanticQuery || filterChips.length > 0 || dealbreakers.mustHave.length > 0 || listingFreshness) && 'lg:hidden'
            )}>
              {/* Mobile home for Sort — see the toolbar copy above. It leads
                  the row so that it AND the Filters pill are both on screen
                  without scrolling; parked after the quick filters it landed
                  ~500px in, i.e. invisible. */}
              <SortSelect className="flex-shrink-0 whitespace-nowrap lg:hidden" onSort={() => handleSearch('sort')} />
              <MobileQuickFilters />
              {parsedCriteria?.semanticQuery && (
                <Badge
                  variant="outline"
                  className="flex-shrink-0 whitespace-nowrap rounded-full border-primary/30 bg-surface text-[12.5px] font-semibold text-primary gap-1 px-3 py-[5px] group"
                  title={t('search.semanticHint', 'Semantic search: results matched by meaning')}
                >
                  <Search className="h-3.5 w-3.5" />
                  {parsedCriteria.semanticQuery}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      // Remove semantic query: convert remaining parsedCriteria to filters
                      if (parsedCriteria) {
                        chipRemovedRef.current = true
                        const remaining = { ...parsedCriteria, semanticQuery: null }
                        updateFilter('rooms_min', remaining.rooms_min)
                        updateFilter('rooms_max', remaining.rooms_max)
                        updateFilter('price_min', remaining.price_min)
                        updateFilter('price_max', remaining.price_max)
                        updateFilter('area_min', remaining.area_min)
                        updateFilter('area_max', remaining.area_max)
                        updateFilter('floor_min', remaining.floor_min)
                        updateFilter('floor_max', remaining.floor_max)
                        if (remaining.city) setSelectedCity(remaining.city)
                        else setSelectedCity('Praha')
                        if (remaining.districts?.length) setSelectedDistricts(remaining.districts)
                        else setSelectedDistricts([])
                        if (remaining.type) updateFilter('type', remaining.type as 'apartment' | 'room' | 'house')
                        if (remaining.transaction_type === 'rent' || remaining.transaction_type === 'sale') {
                          updateFilter('transaction_type', remaining.transaction_type)
                        } else {
                          updateFilter('transaction_type', 'rent')
                        }
                        clearTags()
                        remaining.keywords?.forEach((kw) => addTag(kw))
                        setParsedCriteria(null)
                        setQuery('')
                      }
                    }}
                    className="ml-0.5 -mr-1 rounded-full p-0.5 opacity-50 hover:opacity-100 hover:bg-black/10 dark:hover:bg-white/10 transition-opacity"
                    aria-label={t('search.removeSemantic', 'Remove semantic query')}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {filterChips.length > 0 && (
                <>
                  {filterChips.map((chip) => {
                    const iconMap = {
                      rooms: BedDouble,
                      price: Banknote,
                      area: Maximize2,
                      floor: Layers,
                      location: MapPin,
                      type: Home,
                      feature: Tag,
                      travel: Clock,
                    }
                    const Icon = chip.icon || iconMap[chip.category]

                    return (
                      <Badge
                        key={chip.label}
                        variant="secondary"
                        className={cn(
                          // flex-shrink-0 + nowrap: in the mobile nowrap row a
                          // shrinkable chip wraps its own text into a blob.
                          'flex-shrink-0 whitespace-nowrap rounded-full bg-primary-container text-[12.5px] font-semibold text-on-primary-container px-3 py-[5px] gap-1.5 group',
                          // Rooms/price/location state lives in the quick
                          // pills below lg — showing the chip too is the
                          // double-row redundancy this merge removes.
                          ['rooms', 'price', 'location'].includes(chip.category) && 'hidden lg:inline-flex'
                        )}
                      >
                        <Icon className="h-3.5 w-3.5 flex-shrink-0 opacity-70" />
                        {chip.label}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            chip.onRemove()
                          }}
                          className="ml-0.5 -mr-1 rounded-full p-0.5 opacity-50 hover:opacity-100 hover:bg-black/10 dark:hover:bg-white/10 transition-opacity"
                          aria-label={`${t('search.removeFilter', 'Remove')} ${chip.label} ${t('search.filter', 'filter')}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    )
                  })}
                </>
              )}

              {/* Dealbreaker & freshness chips from Settings (pinned style) */}
              {(dealbreakers.mustHave.length > 0 || listingFreshness) && (
                <>
                  {/* Visual separator between search filters and settings filters */}
                  {(filterChips.length > 0 || parsedCriteria?.semanticQuery) && (
                    <div className="w-px h-5 bg-border mx-0.5 flex-shrink-0" />
                  )}

                  {dealbreakers.mustHave.map((kw) => {
                    const { filterTags } = useKeywordsStore.getState()
                    const tag = filterTags.find((t) => t.id === kw)
                    return (
                      <Link key={`must-${kw}`} href="/settings" className="flex-shrink-0" title={t('search.mustHaveTitle', 'Dealbreaker filter (must have) — click to edit in Settings')}>
                        <Badge
                          variant="outline"
                          className="whitespace-nowrap rounded-full text-[12.5px] font-semibold px-3 py-[5px] gap-1.5 border-dashed border-emerald-500/50 text-emerald-700 dark:text-emerald-400 bg-emerald-50/50 dark:bg-emerald-950/20 hover:bg-emerald-100/50 dark:hover:bg-emerald-950/30 transition-colors cursor-pointer"
                        >
                          <ShieldCheck className="h-3.5 w-3.5 flex-shrink-0" />
                          {tag?.label || kw}
                          <Pin className="h-3 w-3 flex-shrink-0 opacity-40" />
                        </Badge>
                      </Link>
                    )
                  })}


                  {listingFreshness && (
                    <Link href="/settings" className="flex-shrink-0" title={t('search.freshnessTitle', 'Freshness filter — click to edit in Settings')}>
                      <Badge
                        variant="outline"
                        className="whitespace-nowrap rounded-full text-[12.5px] font-semibold px-3 py-[5px] gap-1.5 border-dashed border-amber-500/50 text-amber-700 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-950/20 hover:bg-amber-100/50 dark:hover:bg-amber-950/30 transition-colors cursor-pointer"
                      >
                        <CalendarClock className="h-3.5 w-3.5 flex-shrink-0" />
                        {t('search.lastDays', 'Last')} {listingFreshness} {t('search.days', 'days')}
                        <Pin className="h-3 w-3 flex-shrink-0 opacity-40" />
                      </Badge>
                    </Link>
                  )}
                </>
              )}
            </div>
      </div>

      </div>
      {/* /header column */}

      {/* Results view */}
      {viewMode === 'list' ? (
        <>
          <ListingsGrid />
          {/* Pagination — design search.jsx:592-608 (shared PageNav helper) */}
          {totalPages > 1 && (
            <div className="mt-7 flex flex-wrap items-center justify-center gap-1.5">
              <PageNav
                page={currentPage}
                totalPages={totalPages}
                onChange={handlePageChange}
                disabled={isLoading}
              />

              <span className="ml-2.5 text-xs text-ink-muted">
                {(currentPage - 1) * limit + 1}–{Math.min(currentPage * limit, totalCount)} {t('search.of', 'of')} {totalCount}
                {SORT_OPTIONS.find((o) => o.value === sort) && (
                  <> · {t('search.sortedBy', 'sorted by')} {SORT_OPTIONS.find((o) => o.value === sort)!.label}</>
                )}
              </span>
            </div>
          )}

          {/* Value-anchored jump chips — page numbers mean nothing at depth;
              the sort key's value at 0/25/50/75/100% does. Loaded on demand. */}
          {totalPages > 10 && jumpKind && (
            <JumpWaypoints
              jumpKind={jumpKind}
              waypoints={pageWaypoints}
              loading={waypointsLoading}
              onLoad={loadPageWaypoints}
              pageSize={limit}
              currentPage={currentPage}
              onJump={handlePageChange}
              disabled={isLoading}
              className="mt-2.5"
            />
          )}
        </>
      ) : (
        /* Map view — split on desktop, full-screen on mobile */
        <>
          {/* Mobile: full-screen map (original behaviour) */}
          {/* No -mx-4: the map wrapper no longer applies px-4 (the header
              column owns its padding), so the map is already edge-to-edge. */}
          <div className="md:hidden flex-1 min-h-0 mt-2">
            <ListingMapGoogle
              points={mapPoints}
              onViewportChange={handleMapViewport}
              hydrated={mapHydrated}
              unmappable={mapUnmappable}
              isLoadingMore={mapPointsLoading}
            />
          </div>

          {/* Desktop: district-grouped list + right map (design search.jsx:614-656) */}
          {/* `relative` so the collapsed panel's "Show list" tab can anchor
              to the split's top-left corner over the map. */}
          <div className="relative hidden md:flex flex-1 gap-0 min-h-0 mt-2">
            {/* Left panel: resizable + collapsible shell around the district list */}
            <MapSidePanel>
              <MapDistrictList
                hoveredListingId={hoveredMapListingId}
                selectedListingId={selectedMapListingId}
                onHover={setHoveredMapListingId}
                onSelect={(l) => focusMapListing(l.id)}
                onDistrictHover={setHoveredMapDistrict}
                registerRef={(id, el) => {
                  if (el) cardRefsMap.current.set(id, el)
                  else cardRefsMap.current.delete(id)
                }}
                listings={focusDistrict ? (districtListings ?? []) : mapVisibleListings}
                totalInView={mapInViewCount}
                loading={mapPointsLoading || districtLoading}
                districtCounts={districtCountsMap}
                focusDistrict={focusDistrict}
                onFocusDistrict={(d) => {
                  setFocusDistrict(d)
                  setDistrictShown(DISTRICT_PAGE)
                }}
                onLeaveDistrict={() => setFocusDistrict(null)}
                districtTotal={districtTotal}
                districtShown={districtShown}
                onShowMoreDistrict={() => setDistrictShown((n) => n + DISTRICT_PAGE)}
              />
            </MapSidePanel>

            {/* Right panel: full-height map. The list handle lives HERE, not in
                the panel: it is one control for both states and has to outlive
                the panel unmounting (design web/search.jsx ListHandle). */}
            <div className="flex-1 relative">
              <MapListHandle count={totalCount} />
              <ListingMapGoogle
                mode="split"
                points={mapPoints}
                onViewportChange={handleMapViewportWithDistrict}
                hydrated={mapHydrated}
                unmappable={mapUnmappable}
                isLoadingMore={mapPointsLoading}
                selectedListingId={selectedMapListingId}
                focusNonce={mapFocusNonce}
                fitPoints={districtFitPoints}
                hoveredListingId={hoveredMapListingId}
                hoveredDistrict={hoveredMapDistrict}
                onMarkerHover={handlePinHover}
                onMarkerSelect={(listing) => {
                  setSelectedMapListingId(listing.id)
                  const el = cardRefsMap.current.get(listing.id)
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }}
              />
            </div>
          </div>
        </>
      )}

      {/* Save Search Dialog */}
      <SaveSearchDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
      />

      {/* Mobile View Toggle (Map/List) */}
      <MobileViewToggle />

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 p-3 rounded-full bg-background/80 backdrop-blur-sm border border-border/40 shadow-md text-foreground/70 hover:text-foreground transition-all hover:scale-110 z-50"
          title="Back to top"
          aria-label="Scroll back to top"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}

      {/* Rate limit popup modal */}
      {rateLimitInfo && (
        <RateLimitBanner
          retryAfter={rateLimitInfo.retryAfter}
          dayRemaining={rateLimitInfo.dayRemaining}
          dayLimit={rateLimitInfo.dayLimit}
          onDismiss={() => setRateLimitInfo(null)}
        />
      )}
    </div>
  )
}

export default function SearchPage() {
  return (
    <Suspense fallback={
      <div className="container mx-auto px-4 py-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-muted/60 rounded-lg mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 bg-muted/40 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    }>
      <SearchPageContent />
    </Suspense>
  )
}

